<template>
  <div
    class="sf-sidebar"
    :class="[staticClass, className]"
  >
    <SfOverlay :visible="visibleOverlay" />
    <transition :name="transitionName">
      <aside
        v-if="visible"
        ref="asideContent"
        v-focus-trap
        v-click-outside="checkPersistence"
        class="sf-sidebar__aside bg-EXPONDO-grey-5"
      >
        <slot name="bar">
          <SfBar
            :title="title"
            :back="false"
            :close="true"
            @click:close="close"
          />
        </slot>
        <div
          class="sf-sidebar__content px-2 pb-2 lg:px-4 lg:pb-4"
          tabindex="-1"
        >
          <slot />
        </div>
        <div
          :class="{ 'display-none': !hasBottom }"
          class="sticky top-0 bottom-auto"
        >
          <div
            class="absolute shadow-top top-0 w-full h-1"
          />
          <div
            v-if="$slots['content-bottom']"
            class="p-2 lg:p-4"
          >
            <slot name="content-bottom" />
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script lang="ts">

import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { focusTrap } from '~/directives/focus-trap/focus-trap-directive';
import isClient from '~~/helpers/isClient/isClient';

type ClassBinding = string | Record<string, boolean> | Array<unknown>;

export default defineComponent({
  name: 'SfSidebar',
  directives: { focusTrap },

  props: {
    title: {
      type: String,
      default: '',
    },
    visible: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Boolean,
      default: true,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],
  data() {

    return {
      position: 'left' as 'left' | 'right',
      staticClass: null as string | null,
      className: null as ClassBinding | null,
    };

  },
  computed: {
    visibleOverlay() {

      return this.visible && this.overlay;

    },
    transitionName() {

      return 'sf-slide-' + this.position;

    },
    hasBottom() {

      return 'content-bottom' in this.$slots;

    },
  },
  watch: {
    visible: {
      handler(value) {

        if (!isClient()) {

          return;

        }

        if (value) {

          this.$nextTick(() => {

            const sidebarContent = document.getElementsByClassName(
              'sf-sidebar__content',
            )[0];
            disableBodyScroll(sidebarContent);

          });
          document.addEventListener('keydown', this.keydownHandler);

        } else {

          clearAllBodyScrollLocks();
          document.removeEventListener('keydown', this.keydownHandler);

        }

      },
      immediate: true,
    },
  },
  mounted() {

    this.classHandler();

  },
  updated() {

    this.classHandler();

  },
  beforeUnmount() {

    clearAllBodyScrollLocks();

  },
  methods: {
    close() {

      this.$emit('close');

    },
    checkPersistence() {

      if (!this.persistent) {

        this.close();

      }

    },
    keydownHandler(e: KeyboardEvent) {

      if (e.key === 'Escape' || e.key === 'Esc' || e.keyCode === 27) {

        this.close();

      }

    },
    classHandler() {

      const incomingClass = (this.$attrs.class as ClassBinding | undefined) ?? null;
      const nextStaticClass = typeof incomingClass === 'string' ? incomingClass : null;
      const nextClassName = typeof incomingClass === 'string' ? null : incomingClass;
      let update = false;

      if (this.staticClass !== nextStaticClass) {

        this.staticClass = nextStaticClass;
        update = true;

      }

      if (this.className !== nextClassName) {

        this.className = nextClassName;
        update = true;

      }

      if (update) {

        const normalizedClass = this.flattenClassNames(incomingClass);
        this.position = normalizedClass.includes('--right') ? 'right' : 'left';

      }

    },
    flattenClassNames(value: ClassBinding | null): string {

      if (!value) {

        return '';

      }

      if (typeof value === 'string') {

        return value;

      }

      if (Array.isArray(value)) {

        return value
          .map((item) => this.flattenClassNames(item as ClassBinding | null))
          .filter((item): item is string => !!item)
          .join(' ');

      }

      if (typeof value === 'object') {

        return Object.keys(value)
          .filter((key) => value[key])
          .join(' ');

      }

      return '';

    },
  },
});

</script>

<style lang="scss">
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
@use "~/assets/scss/helpers/_typography" as typography;
.sf-sidebar {
  &__aside {
    position: fixed;
    top: var(--sidebar-top, 0);
    left: var(--sidebar-left, 0);
    right: var(--sidebar-right, 0);
    bottom: var(--sidebar-bottom, 0);
    z-index: var(--sidebar-z-index, 1);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: var(--sidebar-width);
    height: 100%;
    box-shadow: var(--sidebar-box-shadow, 0 4px 11px rgba(29, 31, 34, 0.1));
  }

  &__content {
    overflow: auto;
    display: var(--sidebar-content-display, flex);
    flex-direction: var(--sidebar-content-flex-direction, column);
    box-sizing: border-box;
    width: var(--sidebar-content-width);
    height: 100%;
    scrollbar-color: var(--c-gray-variant) var(--c-light);
    scrollbar-width: thin;
    color: var(--sidebar-content-color, var(--c-text));

    @include typography.font(
      --sidebar-content-font,
      var(--font-weight--medium),
      var(--font-size--base),
      1.6,
      var(--font-family--secondary)
    );

    &::-webkit-scrollbar {
      width: 0;
    }
  }

  @include breakpoints.for-desktop {
    --sidebar-width: 25.5rem;
    --sidebar-content-padding: var(--spacer-base) var(--spacer-xl);

    &--right {
      --sidebar-left: auto;
      --sidebar-right: 0;
      --sidebar-circle-icon-top: var(--spacer-sm);
      --sidebar-content-padding: var(--spacer-base);
    }
  }
}
</style>
